import axiosClient from "@/shared/lib/axios/axios.ts";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@/shared/types/api.ts";
import { API_ROUTES } from "@/polygon/api/config.ts";
import { Polygon, PolygonListResponse, PolygonDetailResponse } from "../../types/polygon.ts";

export interface ICreatePolygonInput {
  name: string;
  checksum: string;
  epsg_code: number;
  file: File;
}

export interface IUpdatePolygonInput {
  name?: string;
  checksum?: string;
  epsg_code?: number;
  file?: File;
}

export interface IPolygonResponse {
  id: number;
  name: string;
  checksum: string;
  epsg_code: number;
  file_url: string;
  created_at: string;
  updated_at: string;
}

// /api/polygon/ is limit/offset-paginated (metadata carries count/next/previous) —
// follow every page so list consumers (polygons table, map polygon selector)
// get all polygons, not just the first page.
const POLYGON_PAGE_LIMIT = 500;

interface PolygonPage<TItem> {
  data: TItem[];
  metadata: { count?: number; next?: string | null; previous?: string | null };
  error?: string | null;
}

const getAllPolygonPages = async <TItem>(
  extraParams?: Record<string, unknown>
): Promise<PolygonPage<TItem>> => {
  const all: TItem[] = [];
  let offset = 0;
  let page: PolygonPage<TItem>;
  do {
    page = await axiosClient
      .get<PolygonPage<TItem>>(`${API_ROUTES.POLYGON}`, {
        params: { limit: POLYGON_PAGE_LIMIT, offset, ...extraParams },
      })
      .then((res) => res.data);
    all.push(...(page.data ?? []));
    offset += POLYGON_PAGE_LIMIT;
    // The empty-page check guards against looping forever on a bad `next`.
  } while (page.metadata?.next && (page.data?.length ?? 0) > 0);
  return { ...page, data: all };
};

// GET /api/polygon/ - List all polygons
export const getAllPolygons = async (): Promise<ApiResponse<IPolygonResponse[]>> => {
  const page = await getAllPolygonPages<IPolygonResponse>();
  // The paginated metadata (count/next/previous) no longer matches
  // ApiResponseMeta; callers only read `data`.
  return page as unknown as ApiResponse<IPolygonResponse[]>;
};

// POST /api/polygon/ - Upload a DXF & create polygon
export const createPolygon = async (data: ICreatePolygonInput): Promise<ApiResponse<IPolygonResponse>> => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("checksum", data.checksum);
  formData.append("epsg_code", data.epsg_code.toString());
  formData.append("file", data.file);

  return axiosClient
    .post<any, AxiosResponse<ApiResponse<IPolygonResponse>>>(
      `${API_ROUTES.POLYGON}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then((res) => res.data);
};

// GET /api/polygon/{id}/ - Retrieve a single polygon
export const getPolygonById = async (id: number): Promise<ApiResponse<IPolygonResponse>> => {
  return axiosClient
    .get<any, AxiosResponse<ApiResponse<IPolygonResponse>>>(
      `${API_ROUTES.POLYGON}${id}/`
    )
    .then((res) => res.data);
};

// PUT /api/polygon/{id}/ - Update a polygon
export const updatePolygon = async (id: number, data: IUpdatePolygonInput): Promise<ApiResponse<IPolygonResponse>> => {
  const formData = new FormData();
  
  if (data.name) formData.append("name", data.name);
  if (data.checksum) formData.append("checksum", data.checksum);
  if (data.epsg_code) formData.append("epsg_code", data.epsg_code.toString());
  if (data.file) formData.append("file", data.file);

  return axiosClient
    .put<any, AxiosResponse<ApiResponse<IPolygonResponse>>>(
      `${API_ROUTES.POLYGON}${id}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then((res) => res.data);
};

// PATCH /api/polygon/{id}/ - Partial update a polygon
export const patchPolygon = async (id: number, data: IUpdatePolygonInput): Promise<ApiResponse<IPolygonResponse>> => {
  const formData = new FormData();
  
  if (data.name) formData.append("name", data.name);
  if (data.checksum) formData.append("checksum", data.checksum);
  if (data.epsg_code) formData.append("epsg_code", data.epsg_code.toString());
  if (data.file) formData.append("file", data.file);

  return axiosClient
    .patch<any, AxiosResponse<ApiResponse<IPolygonResponse>>>(
      `${API_ROUTES.POLYGON}${id}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then((res) => res.data);
};

// DELETE /api/polygon/{id}/ - Delete a polygon
export const deletePolygon = async (id: number): Promise<void> => {
  return axiosClient
    .delete(`${API_ROUTES.POLYGON}${id}/`)
    .then(() => {});
};

// GET /api/polygon/ - List all polygons with coordinates
// Org is scoped via the X-Minemaestro-Organization header. An optional
// location_id narrows the list to a single location's polygons.
export const getAllPolygonsWithCoordinates = async (
  locationId?: number
): Promise<PolygonListResponse> => {
  return getAllPolygonPages<Polygon>(
    locationId != null ? { location_id: locationId } : undefined
  );
};

// GET /api/polygon/{id}/ - Retrieve a single polygon with coordinates
export const getPolygonByIdWithCoordinates = async (id: number): Promise<PolygonDetailResponse> => {
  return axiosClient
    .get<any, AxiosResponse<PolygonDetailResponse>>(
      `${API_ROUTES.POLYGON}${id}/`
    )
    .then((res) => res.data);
};